import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useEffect, useRef } from 'react';
import { Settings, Code, FileText, Users, Wand2, Upload, Type, Link } from 'lucide-react';
import { Button } from '../ui/Button';
import yaml from 'js-yaml';
export function TemplateConfigurator({ templateContent, existingFields, onChange, onYamlChange, onValuesChange, extractedElements }) {
    const [activeSection, setActiveSection] = useState('upload');
    const [yamlContent, setYamlContent] = useState('');
    const [yamlError, setYamlError] = useState(null);
    const [fields, setFields] = useState(existingFields);
    const [preFillMode, setPreFillMode] = useState({});
    const [manualValues, setManualValues] = useState({});
    const [insertPlaceholders, setInsertPlaceholders] = useState([]);
    const fileInputRef = useRef(null);
    // Extract YAML from template on mount
    useEffect(() => {
        extractYamlFromTemplate();
        // Always extract variables and placeholders (we need placeholders even if we have existing fields)
        extractVariablesFromTemplate();
        // If we have existing fields, use them (this will be overridden by extractVariablesFromTemplate if needed)
        if (existingFields.length > 0) {
            setFields(existingFields);
        }
    }, [templateContent]);
    const extractYamlFromTemplate = () => {
        const yamlMatch = templateContent.match(/^---\s*\n([\s\S]*?)\n---/);
        if (yamlMatch) {
            setYamlContent(yamlMatch[1]);
            try {
                const parsed = yaml.load(yamlMatch[1]);
                setYamlError(null);
            }
            catch (err) {
                setYamlError(err.message);
            }
        }
        else {
            // Create default YAML (no hardcoded values - let user fill offering-specific data)
            const defaultYaml = `variables:
  company_name: ""
  offering_name: ""
  date: "{{@today}}"

level-1: "1."
level-2: "a."
level-3: "i."`;
            setYamlContent(defaultYaml);
        }
    };
    const extractVariablesFromTemplate = () => {
        // If we have pre-extracted elements from database, use those
        if (extractedElements) {
            console.log('[TemplateConfigurator] Using pre-extracted elements from database:', extractedElements);
            // Store INSERT placeholders for display
            setInsertPlaceholders(extractedElements.placeholders);
            // Create variable set from extracted elements
            const variableSet = new Set(extractedElements.variables);
            const placeholderSet = new Set(extractedElements.placeholders);
            const conditionalSet = new Set(extractedElements.conditionals || []);
            // Continue with the rest of the function using these sets
            createFieldsFromExtractedElements(variableSet, placeholderSet, conditionalSet);
            return;
        }
        console.log('[TemplateConfigurator] No pre-extracted elements, extracting from template content');
        // Fallback: Extract from template content if no pre-extracted elements
        // Extract YAML section first - we don't count elements in YAML
        const yamlMatch = templateContent.match(/^---\s*\n([\s\S]*?)\n---/);
        const templateBodyOnly = yamlMatch ? templateContent.substring(yamlMatch[0].length) : templateContent;
        const variablePattern = /\{\{([^}]+)\}\}/g;
        const placeholderPattern = /\[([^\]]+)\]/g;
        const variableMatches = templateBodyOnly.matchAll(variablePattern);
        const placeholderMatches = templateBodyOnly.matchAll(placeholderPattern);
        const variableSet = new Set();
        const placeholderSet = new Set();
        const insertSet = new Set();
        const conditionalSet = new Set();
        // Extract {{variables}}
        for (const match of variableMatches) {
            const varName = match[1].trim();
            if (!varName.startsWith('@') && !varName.startsWith('{{')) {
                variableSet.add(varName);
            }
        }
        // Extract [placeholders] - separate conditionals from regular placeholders
        for (const match of placeholderMatches) {
            const content = match[1].trim();
            // Check if it's a conditional (contains {{)
            if (content.includes('{{')) {
                // Extract the variable name from the conditional
                const conditionalMatch = content.match(/\{\{([^}]+)\}\}/);
                if (conditionalMatch) {
                    const varName = conditionalMatch[1].trim();
                    if (!varName.startsWith('@')) {
                        conditionalSet.add(varName);
                    }
                }
            }
            else if (!content.includes('|')) {
                // Regular placeholder (not a cross-reference)
                insertSet.add(content);
                placeholderSet.add(content);
            }
        }
        // Store INSERT placeholders for display
        setInsertPlaceholders(Array.from(insertSet));
        console.log('[TemplateConfigurator] Extracted elements:', {
            insertPlaceholders: Array.from(insertSet),
            placeholderSet: Array.from(placeholderSet),
            variableSet: Array.from(variableSet),
            conditionalSet: Array.from(conditionalSet)
        });
        // Create fields from extracted sets
        createFieldsFromExtractedElements(variableSet, placeholderSet, conditionalSet);
    };
    const createFieldsFromExtractedElements = (variableSet, placeholderSet, conditionalSet) => {
        const newFields = [
            // Variables from {{}}
            ...Array.from(variableSet).map(varName => ({
                variableName: varName,
                label: varName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
                fieldType: 'text',
                fillableBy: ['admin', 'compliance', 'investor'],
                visibleTo: ['admin', 'compliance', 'investor'],
                placeholder: `Enter ${varName.replace(/_/g, ' ')}`,
                helpText: '',
                validation: { required: false }
            })),
            // Placeholder fields from []
            ...Array.from(placeholderSet).map(placeholder => {
                const varName = placeholder.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
                return {
                    variableName: `placeholder_${varName}`,
                    label: placeholder,
                    fieldType: 'textarea',
                    fillableBy: ['admin', 'compliance', 'investor'],
                    visibleTo: ['admin', 'compliance', 'investor'],
                    placeholder: `Enter content for [${placeholder}]`,
                    helpText: `This will replace [${placeholder}] in the document`,
                    validation: { required: false },
                    isComputed: false,
                    description: 'Placeholder field from []'
                };
            }),
            // Conditional fields (boolean type)
            ...(conditionalSet ? Array.from(conditionalSet).map(varName => ({
                variableName: varName,
                label: varName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
                fieldType: 'boolean',
                fillableBy: ['admin', 'compliance', 'investor'],
                visibleTo: ['admin', 'compliance', 'investor'],
                placeholder: '',
                helpText: `Controls conditional section [{{${varName}}} ...]`,
                validation: { required: false },
                isComputed: false,
                description: 'Boolean field for conditional section'
            })) : [])
        ];
        console.log('[TemplateConfigurator] Created fields:', {
            totalFields: newFields.length,
            variableFields: variableSet.size,
            placeholderFields: placeholderSet.size,
            conditionalFields: conditionalSet?.size || 0,
            fields: newFields.map(f => ({ name: f.variableName, type: f.fieldType }))
        });
        setFields(newFields);
    };
    const handleYamlChange = (newYaml) => {
        setYamlContent(newYaml);
        try {
            const parsed = yaml.load(newYaml);
            setYamlError(null);
            onYamlChange?.(parsed);
        }
        catch (err) {
            setYamlError(err.message);
        }
    };
    const handleFieldChange = (index, updates) => {
        const updated = [...fields];
        updated[index] = { ...updated[index], ...updates };
        setFields(updated);
        onChange(updated);
    };
    const handleRoleToggle = (index, role) => {
        const field = fields[index];
        const fillableBy = field.fillableBy || [];
        const newRoles = fillableBy.includes(role)
            ? fillableBy.filter(r => r !== role)
            : [...fillableBy, role];
        handleFieldChange(index, { fillableBy: newRoles });
    };
    const handlePreFillModeChange = (varName, mode) => {
        setPreFillMode(prev => ({ ...prev, [varName]: mode }));
    };
    const handleManualValueChange = (varName, value) => {
        const updatedValues = { ...manualValues, [varName]: value };
        setManualValues(updatedValues);
        // Send updated values back to parent
        onValuesChange?.(updatedValues);
        console.log('[TemplateConfigurator] Manual value changed:', { varName, value, allValues: updatedValues });
    };
    const getRoleBadgeColor = (role) => {
        const colors = {
            admin: '#ef4444',
            compliance: '#f59e0b',
            investor: '#10b981',
            company: '#3b82f6',
            'any-user': '#6b7280'
        };
        return colors[role] || '#6b7280';
    };
    // Download empty template functions
    const downloadTemplate = (format) => {
        try {
            console.log('[TemplateConfigurator] Download template - Current state:', {
                fieldsCount: fields.length,
                insertPlaceholdersCount: insertPlaceholders.length,
                conditionalsCount: fields.filter(f => f.fieldType === 'boolean').length,
                fields: fields.map(f => ({ name: f.variableName, type: f.fieldType })),
                insertPlaceholders: insertPlaceholders
            });
            // Build comprehensive configuration object
            const configData = {};
            // Parse YAML configuration
            let yamlParsed = {};
            try {
                yamlParsed = yaml.load(yamlContent) || {};
            }
            catch (err) {
                console.warn('Could not parse YAML, using empty object');
            }
            // Add YAML variables
            if (yamlParsed.variables) {
                configData.variables = yamlParsed.variables;
            }
            else {
                configData.variables = {};
            }
            // Add all field variables with empty values
            configData.field_values = {};
            fields.forEach(field => {
                configData.field_values[field.variableName] = '';
            });
            // Add role assignments
            configData.role_assignments = {};
            fields.forEach(field => {
                configData.role_assignments[field.variableName] = field.fillableBy || [];
            });
            // Add conditionals (boolean fields)
            configData.conditionals = {};
            fields.filter(f => f.fieldType === 'boolean').forEach(field => {
                configData.conditionals[field.variableName] = false;
            });
            // Add placeholders
            configData.placeholders = {};
            insertPlaceholders.forEach(placeholder => {
                const varName = `placeholder_${placeholder.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '')}`;
                configData.placeholders[varName] = '';
            });
            // Add field configurations
            configData.field_config = {};
            fields.forEach(field => {
                configData.field_config[field.variableName] = {
                    type: field.fieldType,
                    label: field.label,
                    required: field.validation?.required || false,
                    placeholder: field.placeholder || '',
                    helpText: field.helpText || ''
                };
            });
            console.log('[TemplateConfigurator] Download template - configData:', {
                variablesCount: Object.keys(configData.variables).length,
                fieldValuesCount: Object.keys(configData.field_values).length,
                roleAssignmentsCount: Object.keys(configData.role_assignments).length,
                conditionalsCount: Object.keys(configData.conditionals).length,
                placeholdersCount: Object.keys(configData.placeholders).length,
                totalExpected: Object.keys(configData.variables).length +
                    Object.keys(configData.field_values).length +
                    Object.keys(configData.role_assignments).length +
                    Object.keys(configData.conditionals).length +
                    Object.keys(configData.placeholders).length
            });
            let content = '';
            let filename = 'template';
            let mimeType = 'text/plain';
            switch (format) {
                case 'csv':
                    // Create CSV with all configuration sections
                    content = 'Section,Variable,Value\n';
                    // YAML Variables
                    const flattenObject = (obj, prefix = '', section = '') => {
                        for (const key in obj) {
                            const value = obj[key];
                            const fullKey = prefix ? `${prefix}.${key}` : key;
                            if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
                                flattenObject(value, fullKey, section);
                            }
                            else if (Array.isArray(value)) {
                                content += `${section},${fullKey},"${value.join(', ')}"\n`;
                            }
                            else {
                                content += `${section},${fullKey},""\n`;
                            }
                        }
                    };
                    flattenObject(configData.variables, '', 'yaml_variables');
                    flattenObject(configData.field_values, '', 'field_values');
                    flattenObject(configData.role_assignments, '', 'role_assignments');
                    flattenObject(configData.conditionals, '', 'conditionals');
                    flattenObject(configData.placeholders, '', 'placeholders');
                    filename = 'template.csv';
                    mimeType = 'text/csv';
                    break;
                case 'yaml':
                    content = yaml.dump(configData);
                    filename = 'template.yaml';
                    mimeType = 'text/yaml';
                    break;
                case 'json':
                    content = JSON.stringify(configData, null, 2);
                    filename = 'template.json';
                    mimeType = 'application/json';
                    break;
                case 'txt':
                    // Create TXT with all sections
                    content = '# YAML Variables\n';
                    for (const key in configData.variables) {
                        content += `variables.${key}=\n`;
                    }
                    content += '\n# Field Values\n';
                    for (const key in configData.field_values) {
                        content += `field_values.${key}=\n`;
                    }
                    content += '\n# Role Assignments (comma-separated)\n';
                    for (const key in configData.role_assignments) {
                        content += `role_assignments.${key}=\n`;
                    }
                    content += '\n# Conditionals (true/false)\n';
                    for (const key in configData.conditionals) {
                        content += `conditionals.${key}=false\n`;
                    }
                    content += '\n# Placeholders\n';
                    for (const key in configData.placeholders) {
                        content += `placeholders.${key}=\n`;
                    }
                    filename = 'template.txt';
                    mimeType = 'text/plain';
                    break;
            }
            const blob = new Blob([content], { type: mimeType });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        }
        catch (err) {
            console.error('Failed to download template:', err);
            alert('Failed to download template. Please check your configuration.');
        }
    };
    // Upload and populate configuration
    const handleFileUpload = (event) => {
        const file = event.target.files?.[0];
        if (!file)
            return;
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const content = e.target?.result;
                let parsedData;
                if (file.name.endsWith('.json')) {
                    parsedData = JSON.parse(content);
                }
                else if (file.name.endsWith('.yaml') || file.name.endsWith('.yml')) {
                    parsedData = yaml.load(content);
                }
                else if (file.name.endsWith('.csv')) {
                    // Parse CSV with sections
                    const lines = content.split('\n');
                    parsedData = {
                        variables: {},
                        field_values: {},
                        role_assignments: {},
                        conditionals: {},
                        placeholders: {},
                        field_config: {}
                    };
                    for (let i = 1; i < lines.length; i++) {
                        const line = lines[i].trim();
                        if (!line)
                            continue;
                        // Parse CSV: Section,Variable,Value
                        const parts = line.split(',').map(s => s.replace(/^"|"$/g, '').trim());
                        if (parts.length >= 2) {
                            const section = parts[0];
                            const variable = parts[1];
                            const value = parts[2] || '';
                            // Map section to parsedData structure
                            if (section === 'yaml_variables') {
                                parsedData.variables[variable] = value;
                            }
                            else if (section === 'field_values') {
                                parsedData.field_values[variable] = value;
                            }
                            else if (section === 'role_assignments') {
                                // Parse comma-separated roles
                                parsedData.role_assignments[variable] = value ? value.split(',').map(r => r.trim()) : [];
                            }
                            else if (section === 'conditionals') {
                                parsedData.conditionals[variable] = value === 'true';
                            }
                            else if (section === 'placeholders') {
                                parsedData.placeholders[variable] = value;
                            }
                        }
                    }
                }
                else if (file.name.endsWith('.txt')) {
                    // Parse TXT (section-based format)
                    const lines = content.split('\n');
                    parsedData = {
                        variables: {},
                        field_values: {},
                        role_assignments: {},
                        conditionals: {},
                        placeholders: {},
                        field_config: {}
                    };
                    let currentSection = 'field_values'; // Default section
                    for (const line of lines) {
                        const trimmed = line.trim();
                        if (!trimmed || trimmed.startsWith('#'))
                            continue;
                        // Check for section headers like [VARIABLES], [PLACEHOLDERS], etc.
                        const sectionHeaderMatch = trimmed.match(/^\[([^\]]+)\]$/);
                        if (sectionHeaderMatch) {
                            const sectionName = sectionHeaderMatch[1].toLowerCase().trim();
                            // Map section names
                            if (sectionName === 'variables' || sectionName === 'yaml_variables') {
                                currentSection = 'variables';
                            }
                            else if (sectionName === 'field_values' || sectionName === 'field values') {
                                currentSection = 'field_values';
                            }
                            else if (sectionName === 'placeholders') {
                                currentSection = 'placeholders';
                            }
                            else if (sectionName === 'conditionals') {
                                currentSection = 'conditionals';
                            }
                            else if (sectionName === 'role_assignments' || sectionName === 'roles') {
                                currentSection = 'role_assignments';
                            }
                            continue;
                        }
                        const [key, ...valueParts] = trimmed.split('=');
                        const value = valueParts.join('='); // Rejoin in case value contains =
                        if (key) {
                            const parts = key.split('.');
                            let section = currentSection;
                            let variable = key;
                            // Check if format is section.variable=value
                            if (parts.length >= 2) {
                                const possibleSection = parts[0];
                                if (['variables', 'field_values', 'placeholders', 'conditionals', 'role_assignments'].includes(possibleSection)) {
                                    section = possibleSection;
                                    variable = parts.slice(1).join('.');
                                }
                            }
                            const val = value || '';
                            if (section === 'variables') {
                                parsedData.variables[variable] = val;
                            }
                            else if (section === 'field_values') {
                                parsedData.field_values[variable] = val;
                            }
                            else if (section === 'role_assignments') {
                                parsedData.role_assignments[variable] = val ? val.split(',').map(r => r.trim()) : [];
                            }
                            else if (section === 'conditionals') {
                                parsedData.conditionals[variable] = val === 'true';
                            }
                            else if (section === 'placeholders') {
                                // Convert placeholder key to proper format: placeholder_xxx
                                const placeholderKey = variable.startsWith('placeholder_')
                                    ? variable
                                    : `placeholder_${variable.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '')}`;
                                parsedData.placeholders[placeholderKey] = val;
                            }
                        }
                    }
                }
                // Apply the parsed data to the configuration
                if (parsedData) {
                    // Update YAML content with variables section - REPLACE, don't merge
                    let newYamlContent = yamlContent;
                    if (parsedData.variables) {
                        const yamlParsed = yaml.load(yamlContent) || {};
                        yamlParsed.variables = parsedData.variables; // Replace entirely
                        newYamlContent = yaml.dump(yamlParsed);
                        handleYamlChange(newYamlContent);
                    }
                    // Update field values and manual values - REPLACE, don't merge
                    const newManualValues = {};
                    if (parsedData.field_values) {
                        Object.keys(parsedData.field_values).forEach(key => {
                            newManualValues[key] = parsedData.field_values[key];
                        });
                    }
                    // Update conditionals
                    if (parsedData.conditionals) {
                        Object.keys(parsedData.conditionals).forEach(key => {
                            newManualValues[key] = parsedData.conditionals[key];
                        });
                    }
                    // Update placeholders
                    if (parsedData.placeholders) {
                        Object.keys(parsedData.placeholders).forEach(key => {
                            newManualValues[key] = parsedData.placeholders[key];
                        });
                    }
                    setManualValues(newManualValues);
                    // Send updated values back to parent
                    onValuesChange?.(newManualValues);
                    console.log('[TemplateConfigurator] Configuration imported:', {
                        totalValues: Object.keys(newManualValues).length,
                        variables: parsedData.variables ? Object.keys(parsedData.variables).length : 0,
                        fieldValues: parsedData.field_values ? Object.keys(parsedData.field_values).length : 0,
                        conditionals: parsedData.conditionals ? Object.keys(parsedData.conditionals).length : 0,
                        placeholders: parsedData.placeholders ? Object.keys(parsedData.placeholders).length : 0,
                        sampleValues: Object.keys(newManualValues).slice(0, 5).map(k => ({ [k]: newManualValues[k] }))
                    });
                    // Update role assignments
                    if (parsedData.role_assignments) {
                        const updatedFields = [...fields];
                        updatedFields.forEach((field, index) => {
                            if (parsedData.role_assignments[field.variableName]) {
                                updatedFields[index] = {
                                    ...field,
                                    fillableBy: parsedData.role_assignments[field.variableName]
                                };
                            }
                        });
                        setFields(updatedFields);
                        onChange(updatedFields);
                    }
                    alert('Configuration imported successfully! All sections have been populated.');
                }
            }
            catch (err) {
                console.error('Failed to parse file:', err);
                alert('Failed to parse file. Please check the format.');
            }
        };
        reader.readAsText(file);
        // Reset input
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };
    return (_jsxs("div", { className: "space-y-6", children: [_jsxs("div", { className: "flex gap-2 border-b", style: { borderColor: 'var(--border-color)' }, children: [_jsxs("button", { onClick: () => setActiveSection('upload'), className: "px-4 py-3 text-sm font-medium transition-all border-b-2", style: {
                            borderColor: activeSection === 'upload' ? '#D97706' : 'transparent',
                            color: activeSection === 'upload' ? '#D97706' : 'var(--text-secondary)'
                        }, children: [_jsx(Upload, { className: "w-4 h-4 inline mr-2" }), "Upload & Import"] }), _jsxs("button", { onClick: () => setActiveSection('yaml'), className: "px-4 py-3 text-sm font-medium transition-all border-b-2", style: {
                            borderColor: activeSection === 'yaml' ? '#D97706' : 'transparent',
                            color: activeSection === 'yaml' ? '#D97706' : 'var(--text-secondary)'
                        }, children: [_jsx(Code, { className: "w-4 h-4 inline mr-2" }), "YAML Configuration"] }), _jsxs("button", { onClick: () => setActiveSection('variables'), className: "px-4 py-3 text-sm font-medium transition-all border-b-2", style: {
                            borderColor: activeSection === 'variables' ? '#D97706' : 'transparent',
                            color: activeSection === 'variables' ? '#D97706' : 'var(--text-secondary)'
                        }, children: [_jsx(FileText, { className: "w-4 h-4 inline mr-2" }), "Variables & Pre-fill"] }), _jsxs("button", { onClick: () => setActiveSection('roles'), className: "px-4 py-3 text-sm font-medium transition-all border-b-2", style: {
                            borderColor: activeSection === 'roles' ? '#D97706' : 'transparent',
                            color: activeSection === 'roles' ? '#D97706' : 'var(--text-secondary)'
                        }, children: [_jsx(Users, { className: "w-4 h-4 inline mr-2" }), "Role Assignment"] }), _jsxs("button", { onClick: () => setActiveSection('conditionals'), className: "px-4 py-3 text-sm font-medium transition-all border-b-2", style: {
                            borderColor: activeSection === 'conditionals' ? '#D97706' : 'transparent',
                            color: activeSection === 'conditionals' ? '#D97706' : 'var(--text-secondary)'
                        }, children: [_jsx(Settings, { className: "w-4 h-4 inline mr-2" }), "Conditionals"] }), _jsxs("button", { onClick: () => setActiveSection('placeholders'), className: "px-4 py-3 text-sm font-medium transition-all border-b-2", style: {
                            borderColor: activeSection === 'placeholders' ? '#D97706' : 'transparent',
                            color: activeSection === 'placeholders' ? '#D97706' : 'var(--text-secondary)'
                        }, children: [_jsx(Type, { className: "w-4 h-4 inline mr-2" }), "Placeholders []"] })] }), activeSection === 'upload' && (_jsxs("div", { className: "space-y-6", children: [_jsxs("div", { children: [_jsx("h3", { className: "text-lg font-semibold mb-2", style: { color: 'var(--text-primary)' }, children: "Upload & Import Configuration" }), _jsx("p", { className: "text-sm mb-4", style: { color: 'var(--text-secondary)' }, children: "Upload a CSV, YAML, JSON, TXT, or MD file with field values to auto-fill the form" })] }), _jsx("div", { className: "rounded-2xl p-8 text-center", style: {
                            backgroundColor: 'var(--bg-elevated)',
                            border: '2px dashed var(--border-color)'
                        }, children: _jsxs("div", { className: "flex flex-col items-center gap-4", children: [_jsx("div", { className: "w-16 h-16 rounded-full flex items-center justify-center", style: { backgroundColor: '#D9770620' }, children: _jsx(Upload, { className: "w-8 h-8", style: { color: '#D97706' } }) }), _jsxs("div", { children: [_jsx("h4", { className: "text-lg font-semibold mb-2", style: { color: 'var(--text-primary)' }, children: "Choose File to Upload" }), _jsx("p", { className: "text-sm mb-4", style: { color: 'var(--text-secondary)' }, children: "Supports CSV, YAML, JSON, TXT, and MD files" })] }), _jsx("input", { ref: fileInputRef, type: "file", accept: ".csv,.yaml,.yml,.json,.txt,.md", onChange: handleFileUpload, className: "hidden" }), _jsxs(Button, { onClick: () => fileInputRef.current?.click(), style: { backgroundColor: '#D97706', color: 'white', padding: '12px 24px' }, children: [_jsx(Upload, { className: "w-4 h-4 mr-2" }), "Select File"] })] }) }), _jsxs("div", { className: "rounded-2xl p-6", style: {
                            backgroundColor: 'var(--bg-elevated)',
                            border: '1px solid var(--border-color)'
                        }, children: [_jsx("h4", { className: "text-base font-semibold mb-4", style: { color: 'var(--text-primary)' }, children: "Download empty template:" }), _jsxs("div", { className: "flex gap-3 mb-6", children: [_jsx(Button, { variant: "outline", onClick: () => downloadTemplate('csv'), style: { borderColor: '#D97706', color: '#D97706', flex: 1 }, children: "CSV" }), _jsx(Button, { variant: "outline", onClick: () => downloadTemplate('yaml'), style: { borderColor: '#D97706', color: '#D97706', flex: 1 }, children: "YAML" }), _jsx(Button, { variant: "outline", onClick: () => downloadTemplate('json'), style: { borderColor: '#D97706', color: '#D97706', flex: 1 }, children: "JSON" }), _jsx(Button, { variant: "outline", onClick: () => downloadTemplate('txt'), style: { borderColor: '#D97706', color: '#D97706', flex: 1 }, children: "TXT" })] }), _jsxs("div", { className: "rounded-lg p-4", style: {
                                    backgroundColor: 'var(--bg-base)',
                                    border: '1px solid var(--border-color)'
                                }, children: [_jsx("h5", { className: "text-sm font-semibold mb-3", style: { color: 'var(--text-primary)' }, children: "Example formats:" }), _jsxs("div", { className: "space-y-3 text-xs", style: { color: 'var(--text-secondary)' }, children: [_jsxs("div", { children: [_jsx("span", { className: "font-semibold", style: { color: '#10b981' }, children: "CSV:" }), _jsx("pre", { className: "mt-1 p-2 rounded", style: { backgroundColor: 'var(--bg-elevated)', fontFamily: 'monospace' }, children: `Section,Variable,Value
yaml_variables,company_name,COW Group
field_values,investor_name,John Doe
role_assignments,investor_name,admin, investor
conditionals,include_section,true
placeholders,placeholder_intro,Welcome text...` })] }), _jsxs("div", { children: [_jsx("span", { className: "font-semibold", style: { color: '#3b82f6' }, children: "YAML:" }), _jsx("pre", { className: "mt-1 p-2 rounded", style: { backgroundColor: 'var(--bg-elevated)', fontFamily: 'monospace' }, children: `variables:
  company_name: COW Group
field_values:
  investor_name: John Doe
role_assignments:
  investor_name: [admin, investor]
conditionals:
  include_section: true
placeholders:
  placeholder_intro: Welcome text...` })] }), _jsxs("div", { children: [_jsx("span", { className: "font-semibold", style: { color: '#f59e0b' }, children: "JSON:" }), _jsx("pre", { className: "mt-1 p-2 rounded", style: { backgroundColor: 'var(--bg-elevated)', fontFamily: 'monospace' }, children: `{
  "variables": {"company_name": "COW Group"},
  "field_values": {"investor_name": "John Doe"},
  "role_assignments": {"investor_name": ["admin", "investor"]},
  "conditionals": {"include_section": true},
  "placeholders": {"placeholder_intro": "Welcome text..."}
}` })] }), _jsxs("div", { children: [_jsx("span", { className: "font-semibold", style: { color: '#8b5cf6' }, children: "TXT:" }), _jsx("pre", { className: "mt-1 p-2 rounded", style: { backgroundColor: 'var(--bg-elevated)', fontFamily: 'monospace' }, children: `# YAML Variables
variables.company_name=COW Group
# Field Values
field_values.investor_name=John Doe
# Role Assignments
role_assignments.investor_name=admin, investor
# Conditionals
conditionals.include_section=true
# Placeholders
placeholders.placeholder_intro=Welcome text...` })] })] })] })] }), _jsxs("div", { className: "rounded-lg p-4", style: {
                            backgroundColor: '#3b82f610',
                            border: '1px solid #3b82f6'
                        }, children: [_jsxs("h5", { className: "text-sm font-semibold mb-2 flex items-center gap-2", style: { color: '#3b82f6' }, children: [_jsx(FileText, { className: "w-4 h-4" }), "How it works"] }), _jsxs("ul", { className: "text-xs space-y-1", style: { color: 'var(--text-secondary)' }, children: [_jsx("li", { children: "\u2022 Download an empty template in your preferred format (CSV, YAML, JSON, or TXT)" }), _jsx("li", { children: "\u2022 The template includes all configuration sections:" }), _jsx("li", { className: "ml-4", children: "- YAML Variables (company_name, date, etc.)" }), _jsx("li", { className: "ml-4", children: "- Field Values (all template variables)" }), _jsx("li", { className: "ml-4", children: "- Role Assignments (who can fill each field)" }), _jsx("li", { className: "ml-4", children: "- Conditionals (boolean fields for conditional sections)" }), _jsx("li", { className: "ml-4", children: "- Placeholders (content for [] placeholders)" }), _jsx("li", { children: "\u2022 Fill in the values for each section in the downloaded file" }), _jsx("li", { children: "\u2022 Upload the completed file to auto-populate all configuration tabs" }), _jsx("li", { children: "\u2022 All sections will be automatically populated: YAML Configuration, Variables, Role Assignment, Conditionals, and Placeholders" })] })] })] })), activeSection === 'yaml' && (_jsxs("div", { className: "space-y-4", children: [_jsxs("div", { children: [_jsx("h3", { className: "text-lg font-semibold mb-2", style: { color: 'var(--text-primary)' }, children: "YAML Front Matter" }), _jsx("p", { className: "text-sm mb-4", style: { color: 'var(--text-secondary)' }, children: "Configure variables, numbering formats, and conditional logic" })] }), _jsxs("div", { children: [_jsx("textarea", { value: yamlContent, onChange: (e) => handleYamlChange(e.target.value), rows: 15, className: "w-full px-4 py-3 rounded-lg font-mono text-sm", style: {
                                    backgroundColor: 'var(--bg-base)',
                                    border: `1px solid ${yamlError ? '#ef4444' : 'var(--border-color)'}`,
                                    color: 'var(--text-primary)'
                                }, placeholder: "---\nvariables:\n  company_name: 'Your Company'\n  date: '{{@today}}'\n\nlevel-1: '1.'\nlevel-2: 'a.'\n---" }), yamlError && (_jsxs("p", { className: "mt-2 text-sm", style: { color: '#ef4444' }, children: ["YAML Error: ", yamlError] }))] }), _jsxs("div", { className: "rounded-lg p-4", style: { backgroundColor: '#D9770610', border: '1px solid #D9770630' }, children: [_jsx("h4", { className: "text-sm font-semibold mb-2", style: { color: '#D97706' }, children: "YAML Syntax Guide" }), _jsxs("div", { className: "text-xs space-y-1", style: { color: 'var(--text-secondary)' }, children: [_jsxs("p", { children: ["\u2022 ", _jsx("code", { children: "variables:" }), " - Define default values for template variables"] }), _jsxs("p", { children: ["\u2022 ", _jsx("code", { children: "level-1" }), " through ", _jsx("code", { children: "level-6" }), " - Configure section numbering (1., a., i., etc.)"] }), _jsxs("p", { children: ["\u2022 Use ", _jsx("code", { children: '{{@today}}' }), " for automatic current date"] }), _jsx("p", { children: "\u2022 Boolean values for conditionals (true/false)" })] })] })] })), activeSection === 'variables' && (_jsxs("div", { className: "space-y-4", children: [_jsxs("div", { children: [_jsx("h3", { className: "text-lg font-semibold mb-2", style: { color: 'var(--text-primary)' }, children: "Variables & Pre-fill Options" }), _jsx("p", { className: "text-sm mb-4", style: { color: 'var(--text-secondary)' }, children: "Configure how each variable should be filled" })] }), _jsx("div", { className: "space-y-4", children: fields.map((field, index) => (_jsxs("div", { className: "p-4 rounded-lg", style: {
                                backgroundColor: 'var(--bg-base)',
                                border: '1px solid var(--border-color)'
                            }, children: [_jsxs("div", { className: "flex items-start justify-between mb-3", children: [_jsxs("div", { children: [_jsx("h4", { className: "font-semibold text-sm", style: { color: 'var(--text-primary)' }, children: `{{${field.variableName}}}` }), _jsx("p", { className: "text-xs", style: { color: 'var(--text-secondary)' }, children: field.label })] }), _jsxs("div", { className: "flex gap-2", children: [_jsxs("button", { onClick: () => handlePreFillModeChange(field.variableName, 'manual'), className: "px-3 py-1 rounded text-xs font-medium transition-all", style: {
                                                        backgroundColor: preFillMode[field.variableName] === 'manual' ? '#D97706' : 'var(--bg-elevated)',
                                                        color: preFillMode[field.variableName] === 'manual' ? 'white' : 'var(--text-secondary)'
                                                    }, children: [_jsx(Type, { className: "w-3 h-3 inline mr-1" }), "Manual"] }), _jsxs("button", { onClick: () => handlePreFillModeChange(field.variableName, 'form'), className: "px-3 py-1 rounded text-xs font-medium transition-all", style: {
                                                        backgroundColor: preFillMode[field.variableName] === 'form' ? '#D97706' : 'var(--bg-elevated)',
                                                        color: preFillMode[field.variableName] === 'form' ? 'white' : 'var(--text-secondary)'
                                                    }, children: [_jsx(FileText, { className: "w-3 h-3 inline mr-1" }), "Form"] }), _jsxs("button", { onClick: () => handlePreFillModeChange(field.variableName, 'ai'), className: "px-3 py-1 rounded text-xs font-medium transition-all", style: {
                                                        backgroundColor: preFillMode[field.variableName] === 'ai' ? '#D97706' : 'var(--bg-elevated)',
                                                        color: preFillMode[field.variableName] === 'ai' ? 'white' : 'var(--text-secondary)'
                                                    }, children: [_jsx(Wand2, { className: "w-3 h-3 inline mr-1" }), "AI"] })] })] }), preFillMode[field.variableName] === 'manual' && (_jsx("div", { className: "mt-3", children: _jsx("input", { type: "text", value: manualValues[field.variableName] || '', onChange: (e) => handleManualValueChange(field.variableName, e.target.value), placeholder: `Enter value for ${field.label}`, className: "w-full px-3 py-2 rounded text-sm", style: {
                                            backgroundColor: 'var(--bg-elevated)',
                                            border: '1px solid var(--border-color)',
                                            color: 'var(--text-primary)'
                                        } }) })), preFillMode[field.variableName] === 'form' && (_jsxs("div", { className: "mt-3 flex items-center gap-2 text-xs", style: { color: 'var(--text-secondary)' }, children: [_jsx(Link, { className: "w-4 h-4" }), _jsx("span", { children: "Will be filled from Fill Form tab" })] })), preFillMode[field.variableName] === 'ai' && (_jsxs("div", { className: "mt-3 flex items-center gap-2 text-xs", style: { color: 'var(--text-secondary)' }, children: [_jsx(Wand2, { className: "w-4 h-4" }), _jsx("span", { children: "Will use AI to generate appropriate value" })] })), _jsxs("div", { className: "mt-3 grid grid-cols-2 gap-3", children: [_jsxs("div", { children: [_jsx("label", { className: "text-xs font-medium block mb-1", style: { color: 'var(--text-secondary)' }, children: "Field Type" }), _jsxs("select", { value: field.fieldType, onChange: (e) => handleFieldChange(index, { fieldType: e.target.value }), className: "w-full px-2 py-1 rounded text-xs", style: {
                                                        backgroundColor: 'var(--bg-elevated)',
                                                        border: '1px solid var(--border-color)',
                                                        color: 'var(--text-primary)'
                                                    }, children: [_jsx("option", { value: "text", children: "Text" }), _jsx("option", { value: "number", children: "Number" }), _jsx("option", { value: "date", children: "Date" }), _jsx("option", { value: "boolean", children: "Boolean" }), _jsx("option", { value: "select", children: "Select" }), _jsx("option", { value: "textarea", children: "Text Area" }), _jsx("option", { value: "signature", children: "Signature" }), _jsx("option", { value: "image", children: "Image Upload" })] })] }), _jsxs("div", { children: [_jsx("label", { className: "text-xs font-medium block mb-1", style: { color: 'var(--text-secondary)' }, children: "Required" }), _jsxs("label", { className: "flex items-center gap-2", children: [_jsx("input", { type: "checkbox", checked: field.validation?.required || false, onChange: (e) => handleFieldChange(index, { validation: { ...field.validation, required: e.target.checked } }), className: "rounded" }), _jsx("span", { className: "text-xs", children: "Required field" })] })] })] })] }, field.variableName))) })] })), activeSection === 'roles' && (_jsxs("div", { className: "space-y-4", children: [_jsxs("div", { children: [_jsx("h3", { className: "text-lg font-semibold mb-2", style: { color: 'var(--text-primary)' }, children: "Field Access by Role" }), _jsx("p", { className: "text-sm mb-4", style: { color: 'var(--text-secondary)' }, children: "Assign which roles can fill each field" })] }), _jsx("div", { className: "space-y-3", children: fields.map((field, index) => (_jsxs("div", { className: "p-4 rounded-lg", style: {
                                backgroundColor: 'var(--bg-base)',
                                border: '1px solid var(--border-color)'
                            }, children: [_jsxs("div", { className: "flex items-center justify-between", children: [_jsxs("div", { children: [_jsx("h4", { className: "font-semibold text-sm", style: { color: 'var(--text-primary)' }, children: field.label }), _jsx("p", { className: "text-xs", style: { color: 'var(--text-secondary)' }, children: `{{${field.variableName}}}` })] }), _jsx("div", { className: "flex gap-2", children: ['admin', 'compliance', 'investor', 'company'].map(role => (_jsx("button", { onClick: () => handleRoleToggle(index, role), className: "px-3 py-1 rounded-full text-xs font-medium transition-all", style: {
                                                    backgroundColor: field.fillableBy?.includes(role) ? getRoleBadgeColor(role) : 'var(--bg-elevated)',
                                                    color: field.fillableBy?.includes(role) ? 'white' : 'var(--text-secondary)',
                                                    border: `1px solid ${field.fillableBy?.includes(role) ? getRoleBadgeColor(role) : 'var(--border-color)'}`
                                                }, children: role.charAt(0).toUpperCase() + role.slice(1) }, role))) })] }), field.fillableBy && field.fillableBy.length > 0 && (_jsxs("div", { className: "mt-2 flex items-center gap-2", children: [_jsx(Link, { className: "w-3 h-3", style: { color: '#D97706' } }), _jsx("a", { href: `#fill-form-${field.variableName}`, className: "text-xs hover:underline", style: { color: '#D97706' }, children: "Go to form field \u2192" })] }))] }, field.variableName))) })] })), activeSection === 'conditionals' && (_jsxs("div", { className: "space-y-4", children: [_jsxs("div", { children: [_jsx("h3", { className: "text-lg font-semibold mb-2", style: { color: 'var(--text-primary)' }, children: "Conditional Sections" }), _jsx("p", { className: "text-sm mb-4", style: { color: 'var(--text-secondary)' }, children: "Configure boolean variables for conditional content" })] }), _jsxs("div", { className: "space-y-3", children: [fields.filter(f => f.fieldType === 'boolean').map((field, index) => (_jsxs("div", { className: "p-4 rounded-lg flex items-center justify-between", style: {
                                    backgroundColor: 'var(--bg-base)',
                                    border: '1px solid var(--border-color)'
                                }, children: [_jsxs("div", { children: [_jsx("h4", { className: "font-semibold text-sm", style: { color: 'var(--text-primary)' }, children: field.label }), _jsxs("p", { className: "text-xs", style: { color: 'var(--text-secondary)' }, children: ["[", `{{${field.variableName}}}`, " conditional content]"] })] }), _jsxs("label", { className: "flex items-center gap-2", children: [_jsx("input", { type: "checkbox", checked: manualValues[field.variableName] === true, onChange: (e) => handleManualValueChange(field.variableName, e.target.checked), className: "rounded" }), _jsx("span", { className: "text-sm", children: "Include section" })] })] }, field.variableName))), fields.filter(f => f.fieldType === 'boolean').length === 0 && (_jsxs("div", { className: "text-center py-8", style: { color: 'var(--text-secondary)' }, children: [_jsx("p", { className: "text-sm", children: "No conditional sections defined" }), _jsx("p", { className: "text-xs mt-2", children: "Add boolean fields in the Variables tab to create conditionals" })] }))] })] })), activeSection === 'placeholders' && (_jsxs("div", { className: "space-y-4", children: [_jsxs("div", { children: [_jsx("h3", { className: "text-lg font-semibold mb-2", style: { color: 'var(--text-primary)' }, children: "Placeholders []" }), _jsx("p", { className: "text-sm mb-4", style: { color: 'var(--text-secondary)' }, children: "Fill in content for each placeholder. These values will be available in the Fill Form tab and will replace the placeholders in the final document." })] }), insertPlaceholders.length > 0 ? (_jsxs("div", { className: "space-y-3", children: [insertPlaceholders.map((placeholder, index) => {
                                const varName = `placeholder_${placeholder.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '')}`;
                                return (_jsxs("div", { className: "p-4 rounded-lg", style: {
                                        backgroundColor: 'var(--bg-base)',
                                        border: '1px solid #f59e0b'
                                    }, children: [_jsxs("div", { className: "flex items-start gap-3 mb-3", children: [_jsx("div", { className: "flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center", style: { backgroundColor: '#f59e0b', color: 'white' }, children: index + 1 }), _jsxs("div", { className: "flex-1", children: [_jsxs("code", { className: "text-sm font-mono font-semibold", style: { color: '#f59e0b' }, children: ["[", placeholder, "]"] }), _jsx("p", { className: "text-xs mt-1", style: { color: 'var(--text-secondary)' }, children: "Enter the content that will replace this placeholder" })] })] }), _jsx("textarea", { value: manualValues[varName] || '', onChange: (e) => handleManualValueChange(varName, e.target.value), rows: 4, placeholder: `Enter content for [${placeholder}]`, className: "w-full px-3 py-2 rounded text-sm", style: {
                                                backgroundColor: 'var(--bg-elevated)',
                                                border: '1px solid var(--border-color)',
                                                color: 'var(--text-primary)'
                                            } })] }, index));
                            }), _jsxs("div", { className: "p-4 rounded-lg", style: {
                                    backgroundColor: '#3b82f610',
                                    border: '1px solid #3b82f6'
                                }, children: [_jsx("h4", { className: "font-semibold text-sm mb-2", style: { color: '#3b82f6' }, children: "\uD83D\uDCA1 How Placeholders Work" }), _jsxs("ul", { className: "text-xs space-y-2", style: { color: 'var(--text-secondary)' }, children: [_jsx("li", { children: "\u2022 Fill in content here or in the Fill Form tab" }), _jsx("li", { children: "\u2022 Values entered here will be pre-filled in forms" }), _jsx("li", { children: "\u2022 Placeholders will be replaced with your content in the final document" }), _jsx("li", { children: "\u2022 Use detailed, specific content rather than generic text" })] })] })] })) : (_jsxs("div", { className: "text-center py-8", style: { color: 'var(--text-secondary)' }, children: [_jsx("div", { className: "inline-flex items-center justify-center w-16 h-16 rounded-full mb-4", style: { backgroundColor: '#10b98120' }, children: _jsx("svg", { className: "w-8 h-8", style: { color: '#10b981' }, fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M5 13l4 4L19 7" }) }) }), _jsx("p", { className: "text-sm font-semibold", children: "No Placeholders Found" }), _jsx("p", { className: "text-xs mt-2", children: "This template doesn't use [] placeholders" })] }))] }))] }));
}
